import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import optionTypes from '../constants/option-types';
import ElementSettingsContext from '../helpers/element-settings-context';
import { getOptionFlatAttributesValues } from '../helpers/get-option-attributes-values';
import mappingOptionElementIdsToAttributes from '../helpers/mapping-option-element-ids-to-attributes';
import getOptionBatchChanges from '../helpers/get-option-batch-changes';
import modifyOptionBatchChanges from '../helpers/modify-option-batch-changes';

const { ToggleSection, Stack, Box } = designSystem;

function ToggleableSectionOption({ children, option, settingId, onUpdateState }) {
  const { state, callbacks } = useContext(ElementSettingsContext);

  const elementIdsToAttributes = mappingOptionElementIdsToAttributes(option);
  const flattenedAttributesValues = getOptionFlatAttributesValues(state, option);
  const areAllValuesBooleans = flattenedAttributesValues.every(
    (value) => typeof value === 'boolean'
  );
  const isToggleEnabled = areAllValuesBooleans
    ? flattenedAttributesValues.every((value) => value !== false)
    : flattenedAttributesValues.some((value) => value !== false);

  const paddingProps = {
    paddingTop: 'spacing-xs',
    paddingBottom: isToggleEnabled ? 'spacing-s' : 'spacing-xs',
    paddingLeft: 'spacing-m',
    paddingRight: 'spacing-m',
  };

  const handleToggleChange = (newValue) => {
    const batchChanges = modifyOptionBatchChanges({
      batch: getOptionBatchChanges({
        elementIds: option.elementIds,
        attributes: option.attributes,
        value: newValue,
      }),
      onFilterCallback: ({ elementId, attribute }) =>
        elementIdsToAttributes[elementId].includes(attribute),
      onComputeValueCallback: ({ elementId, attribute, value }) =>
        value && option.computeFallbackValue
          ? option.computeFallbackValue({ elementId, attribute })
          : value,
    });

    onUpdateState(batchChanges);
    callbacks.onToggleChange({ optionId: option.id, settingId, isActive: newValue });
  };

  return (
    <Box {...paddingProps}>
      <ToggleSection
        value={isToggleEnabled}
        title={option.title}
        {...(option.popover && { popoverProps: option.popover })}
        onChange={handleToggleChange}
      >
        <Stack gap="spacing-2-xs">{children}</Stack>
      </ToggleSection>
    </Box>
  );
}

ToggleableSectionOption.propTypes = {
  children: PropTypes.node.isRequired,
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: optionTypes.TOGGLEABLE_SECTION.isRequired,
    elementIds: PropTypes.arrayOf(PropTypes.string),
    attributes: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.array.isRequired,
    title: PropTypes.string,
    popover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      imgSrc: PropTypes.string,
    }),
    computeFallbackValue: PropTypes.func,
  }).isRequired,
  settingId: PropTypes.string.isRequired,
  onUpdateState: PropTypes.func.isRequired,
};

export default ToggleableSectionOption;
